@file:OptIn(ExperimentalFoundationApi::class)

package org.teddapp.project.ui.notes

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.input.InputTransformation
import androidx.compose.foundation.text.input.TextFieldBuffer
import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isCtrlPressed
import androidx.compose.ui.input.key.isMetaPressed
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/**
 * Applique [edit] en ne remplaçant que ce qui change. L'historique d'annulation garde
 * ainsi un geste de mise en forme comme un seul pas, et le clavier ne perd pas le fil.
 */
internal fun TextFieldBuffer.applyEdit(edit: MdEdit) {
    val old = asCharSequence().toString()
    val new = edit.text
    var p = 0
    while (p < old.length && p < new.length && old[p] == new[p]) p++
    var q = 0
    while (q < old.length - p && q < new.length - p && old[old.length - 1 - q] == new[new.length - 1 - q]) q++
    replace(p, old.length - q, new.substring(p, new.length - q))
    selection = TextRange(edit.start.coerceIn(0, length), edit.end.coerceIn(0, length))
}

/** Une mise en forme appliquée au texte et à la sélection courants. */
internal fun TextFieldState.format(op: (text: String, start: Int, end: Int) -> MdEdit) {
    val sel = selection
    val edit = op(text.toString(), sel.min, sel.max)
    edit { applyEdit(edit) }
}

/** Entrée dans une liste appelle l'élément suivant ; sur un élément vide, elle en sort. */
internal object ListContinuation : InputTransformation {
    override fun TextFieldBuffer.transformInput() {
        // Seule une frappe d'Entrée nous concerne : un caractère de plus, un retour à la
        // ligne juste avant le curseur, et rien d'autre de changé.
        if (length != originalText.length + 1 || !selection.collapsed || changes.changeCount != 1) return
        val c = selection.start
        val now = asCharSequence()
        if (c == 0 || now[c - 1] != '\n') return
        val edit = MarkdownEdits.continueList(now.toString(), c) ?: return
        applyEdit(edit)
    }
}

/**
 * Les raccourcis des traitements de texte : Ctrl ou Cmd + B, I, K, E, Maj+X pour le
 * barré et Maj+H pour le surligné. Annuler (Ctrl+Z) est déjà pris en charge par le champ.
 */
internal fun Modifier.markdownShortcuts(state: TextFieldState): Modifier = onPreviewKeyEvent { e ->
    if (e.type != KeyEventType.KeyDown || !(e.isCtrlPressed || e.isMetaPressed)) return@onPreviewKeyEvent false
    when {
        e.key == Key.B -> state.format { t, s, en -> MarkdownEdits.toggleWrap(t, s, en, "**") }
        e.key == Key.I -> state.format { t, s, en -> MarkdownEdits.toggleWrap(t, s, en, "_") }
        e.key == Key.K -> state.format(MarkdownEdits::link)
        e.key == Key.E -> state.format(MarkdownEdits::code)
        e.key == Key.H && e.isShiftPressed -> state.format { t, s, en -> MarkdownEdits.toggleWrap(t, s, en, "==") }
        e.key == Key.X && e.isShiftPressed -> state.format { t, s, en -> MarkdownEdits.toggleWrap(t, s, en, "~~") }
        else -> return@onPreviewKeyEvent false
    }
    true
}

/**
 * La barre de mise en forme. Elle défile sur un écran étroit plutôt que de passer à la
 * ligne : les gestes les plus fréquents (annuler, gras, liste) sont à gauche, sous le pouce.
 */
@Composable
internal fun NoteFormatBar(
    state: TextFieldState,
    modifier: Modifier = Modifier,
    compact: Boolean = false,
    onDraw: (() -> Unit)? = null,
    onImage: (() -> Unit)? = null,
    /** Les titres à proposer pour un lien `[[` en cours ; null sans liens. */
    linkTitles: ((String) -> List<String>)? = null,
) {
    // Sous le pouce, des cibles larges ; à la souris, une barre qui tient dans la page.
    val size = if (compact) 36.dp else 28.dp
    // Pendant qu'on écrit `[[…`, la barre propose les notes à lier à la place des outils.
    val query = linkTitles?.let { NoteLinks.typedQuery(state.text.toString(), state.selection.max) }
    if (query != null && linkTitles != null) {
        LinkSuggestions(state, query, linkTitles(query), size, modifier)
        return
    }
    Row(
        modifier = modifier
            .fillMaxWidth()
            .horizontalScroll(rememberScrollState())
            .padding(horizontal = TeddSpace.S1),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(if (compact) 2.dp else 0.dp),
    ) {
        val undo = state.undoState
        // Dessiner d'abord : au stylet, c'est le geste qu'on vient chercher.
        if (onDraw != null) IconTool("draw", size = size) { onDraw() }
        if (onImage != null) IconTool("image", size = size) { onImage() }
        if (linkTitles != null) {
            // Ouvre un lien : la barre passe aussitôt aux propositions.
            IconTool("notes", size = size) { state.format { t, s, e -> MdEdit(t.substring(0, s) + "[[" + t.substring(e), s + 2) } }
        }
        if (onDraw != null || onImage != null) Separator()
        IconTool("undo", enabled = undo.canUndo, size = size) { undo.undo() }
        IconTool("redo", enabled = undo.canRedo, size = size) { undo.redo() }
        Separator()
        GlyphTool("B", weight = FontWeight.Bold, size = size) { state.format { t, s, e -> MarkdownEdits.toggleWrap(t, s, e, "**") } }
        GlyphTool("I", italic = true, size = size) { state.format { t, s, e -> MarkdownEdits.toggleWrap(t, s, e, "_") } }
        GlyphTool("S", strike = true, size = size) { state.format { t, s, e -> MarkdownEdits.toggleWrap(t, s, e, "~~") } }
        IconTool("highlight", size = size) { state.format { t, s, e -> MarkdownEdits.toggleWrap(t, s, e, "==") } }
        Separator()
        GlyphTool("H1", size = size) { state.format { t, s, e -> MarkdownEdits.toggleLine(t, s, e, MdLine.H1) } }
        GlyphTool("H2", size = size) { state.format { t, s, e -> MarkdownEdits.toggleLine(t, s, e, MdLine.H2) } }
        GlyphTool("H3", size = size) { state.format { t, s, e -> MarkdownEdits.toggleLine(t, s, e, MdLine.H3) } }
        Separator()
        IconTool("list-bullet", size = size) { state.format { t, s, e -> MarkdownEdits.toggleLine(t, s, e, MdLine.Bullet) } }
        IconTool("list-ordered", size = size) { state.format { t, s, e -> MarkdownEdits.toggleLine(t, s, e, MdLine.Numbered) } }
        IconTool("task", size = size) { state.format { t, s, e -> MarkdownEdits.toggleLine(t, s, e, MdLine.Task) } }
        IconTool("quote", size = size) { state.format { t, s, e -> MarkdownEdits.toggleLine(t, s, e, MdLine.Quote) } }
        Separator()
        IconTool("code", size = size) { state.format(MarkdownEdits::code) }
        IconTool("link", size = size) { state.format(MarkdownEdits::link) }
        IconTool("minus", size = size) { state.format { t, s, _ -> MarkdownEdits.rule(t, s) } }
    }
}

// Un outil ne prend jamais le focus : sinon un clic le retire au texte, la frappe suivante
// part dans le vide, et Entrée réappuie sur l'outil au lieu d'aller à la ligne.
private val NoFocus = Modifier.focusProperties { canFocus = false }

/** Les notes qui répondent au lien en cours ; une touche complète le lien. */
@Composable
private fun LinkSuggestions(state: TextFieldState, query: String, titles: List<String>, size: androidx.compose.ui.unit.Dp, modifier: Modifier) {
    Row(
        modifier = modifier.fillMaxWidth().horizontalScroll(rememberScrollState()).padding(horizontal = TeddSpace.S2),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Box(Modifier.height(size), contentAlignment = Alignment.Center) {
            Text("Lier à", style = TeddType.Caption, color = TeddTokens.InkMuted)
        }
        val choices = if (query.isNotBlank() && titles.none { NoteLinks.key(it) == NoteLinks.key(query) }) titles + query.trim() else titles
        if (choices.isEmpty()) {
            Text("Tape le titre d'une note", style = TeddType.Caption, color = TeddTokens.InkSubtle)
        }
        choices.forEach { title ->
            val isNew = title !in titles
            Box(
                modifier = NoFocus
                    .height(size - 4.dp)
                    .background(TeddTokens.SurfaceSunken, androidx.compose.foundation.shape.RoundedCornerShape(TeddTokens.RSm))
                    .clickable { state.format { t, _, e -> NoteLinks.complete(t, e, title) } }
                    .padding(horizontal = TeddSpace.S2),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    if (isNew) "+ « $title »" else title,
                    style = TeddType.Caption,
                    color = if (isNew) TeddTokens.InkMuted else TeddTokens.Ink,
                    maxLines = 1,
                )
            }
        }
    }
}

@Composable
private fun IconTool(icon: String, enabled: Boolean = true, size: Dp, onClick: () -> Unit) {
    TeddIconButton(
        onClick = { if (enabled) onClick() },
        size = size,
        modifier = NoFocus.alpha(if (enabled) 1f else 0.35f),
    ) {
        TeddIcon(name = icon, size = 18.dp, color = TeddTokens.InkMuted)
    }
}

@Composable
private fun GlyphTool(
    glyph: String,
    weight: FontWeight = FontWeight.Medium,
    italic: Boolean = false,
    strike: Boolean = false,
    size: Dp,
    onClick: () -> Unit,
) {
    TeddIconButton(onClick = onClick, size = size, modifier = NoFocus) {
        Text(
            text = glyph,
            color = TeddTokens.InkMuted,
            style = TeddType.Label.copy(
                fontSize = if (glyph.length > 1) 13.sp else 16.sp,
                fontWeight = weight,
                fontStyle = if (italic) FontStyle.Italic else FontStyle.Normal,
                textDecoration = if (strike) TextDecoration.LineThrough else null,
            ),
        )
    }
}

@Composable
private fun Separator() {
    Box(Modifier.padding(horizontal = 3.dp).width(1.dp).height(20.dp).background(TeddTokens.Line))
}
